import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Calendar } from 'lucide-react';
import { DentalOfficeAppointmentWithDetails } from '@/types';

interface AppointmentListProps {
  appointments: DentalOfficeAppointmentWithDetails[];
  loading?: boolean;
}

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  confirmed: 'bg-green-100 text-green-800',
  arrived: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  no_show: 'bg-orange-100 text-orange-800',
};

const STATUS_LABELS = {
  scheduled: 'Pantað',
  confirmed: 'Staðfest',
  arrived: 'Mætt',
  in_progress: 'Í gangi',
  completed: 'Lokið',
  cancelled: 'Aflýst',
  no_show: 'Mætti ekki',
};

export function AppointmentList({ appointments, loading }: AppointmentListProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
          <div className="h-5 bg-gray-200 rounded w-20 animate-pulse"></div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
              <div className="h-5 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Tímapantanir í dag</h2>
          <Link
            to="/appointments"
            className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium flex items-center gap-1"
          >
            Sjá allt
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Engar tímapantanir í dag</p>
          <Link
            to="/appointments/new"
            className="inline-block mt-4 text-brand-primary hover:text-brand-primary/80 font-medium text-sm"
          >
            Búa til tímapöntun
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Tímapantanir í dag</h2>
        <Link
          to="/appointments"
          className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium flex items-center gap-1"
        >
          Sjá allt
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="space-y-4">
        {appointments.map((appointment) => (
          <div
            key={appointment.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-brand-primary/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-900">
                    {appointment.start_time?.substring(0, 5) || 'Ekki tiltekinn'}
                  </span>
                </div>
                <p className="text-base font-medium text-gray-900 mb-1">
                  {appointment.patient_name || 'Ónafngreindur sjúklingur'}
                </p>
                {appointment.notes && (
                  <p className="text-sm text-gray-600">{appointment.notes}</p>
                )}
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  STATUS_COLORS[appointment.status] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {STATUS_LABELS[appointment.status] || appointment.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
